import { create } from "zustand";

/**
 * High-visibility axis — orthogonal to theme (dark/light) and skin
 * (clean/instrument). Off by default; when on, punches up the stage/timeline
 * identity colours and raises text + border contrast, layered purely as
 * `[data-hv="on"]` token overrides in tokens.css (never a component fork).
 *
 * Meant for reading the grid across a bright production office or a sunlit
 * FOH position during the festival. Persisted under localStorage key
 * `trace-hv`; applied as the `data-hv` attribute on <html> ("on" only; off =
 * attribute absent). The pre-paint script in index.html mirrors this so the
 * choice survives a reload with no flash.
 */
export type Visibility = "on" | "off";

interface VisibilityState {
  visibility: Visibility;
  toggle: () => void;
  set: (v: Visibility) => void;
}

function applyVisibility(v: Visibility) {
  const el = document.documentElement;
  if (v === "on") {
    el.setAttribute("data-hv", "on");
  } else {
    el.removeAttribute("data-hv");
  }
  localStorage.setItem("trace-hv", v);
}

const stored = localStorage.getItem("trace-hv") as Visibility | null;
const initial: Visibility = stored === "on" ? "on" : "off";
applyVisibility(initial);

export const useVisibilityStore = create<VisibilityState>((set, get) => ({
  visibility: initial,
  toggle: () => {
    const next = get().visibility === "on" ? "off" : "on";
    applyVisibility(next);
    set({ visibility: next });
  },
  set: (visibility: Visibility) => {
    applyVisibility(visibility);
    set({ visibility });
  },
}));
